<template>
  <div class="automated-email-outbox-ui">
    <VFallbackText v-if="isLoading" class="centered">Loading…</VFallbackText>

    <template v-else>
      <div class="automated-email-outbox-ui__filters">
        <span class="automated-email-outbox-ui__filters-label">Filter by type:</span>
        <button
          v-for="filter in TRIGGER_FILTERS"
          :key="filter.id"
          :class="[
            'automated-email-outbox-ui__filter-chip',
            filterTriggerName === filter.value && 'automated-email-outbox-ui__filter-chip--active',
          ]"
          @click="setFilterTriggerName(filter.value)"
        >
          {{ filter.label }}
        </button>
      </div>

      <VFallbackText v-if="!hasDisplayEmails" class="auto-email-outbox">
        {{
          shouldShowEmptyListHint
            ? 'No outbox emails match the current filters.'
            : 'Triggers run once daily. Come back after the next scheduled run to review and send emails to your contacts.'
        }}
      </VFallbackText>

      <template v-else>
      <div class="automated-email-outbox-ui__bulk-bar">
        <span class="automated-email-outbox-ui__bulk-count">
          {{ selectedCount }} emails selected
        </span>
        <template v-if="hasSelection">
          <VBtn flat class="auto-email-table-action" @click="clearSelection">
            Clear selection
          </VBtn>
          <VBtn class="auto-email-action-button" :loading="isSending" @click="sendSelected">
            Send selected ({{ selectedCount }})
          </VBtn>
        </template>
      </div>

      <div class="automated-email-outbox-ui__table" @click="handleTableRowClick">
        <div class="automated-email-outbox-ui__table-header">
          <VTableHeader class="auto-email-outbox" :titles="TABLE_HEADERS">
            <template #col-0>
              <VCheckbox :model-value="allSelected" @change="toggleSelectAll" />
            </template>
          </VTableHeader>
        </div>

        <VTable :table="table" class="auto-email-outbox">
          <template #col-0="{ colData }">
            <div class="auto-email-outbox__checkbox" @click.stop>
              <VCheckbox :model-value="isSelected(colData)" @change="toggleSelection(colData)" />
            </div>
          </template>

          <template #col-1="{ rowIndex }">
            <div v-if="rowViewModels[rowIndex]" class="automated-email-outbox-ui__recipient-cell">
              <span class="automated-email-outbox-ui__recipient-name">
                {{ rowViewModels[rowIndex].recipient.name }}
              </span>
              <span class="automated-email-outbox-ui__recipient-address">
                {{ rowViewModels[rowIndex].recipient.address }}
              </span>
              <span class="automated-email-outbox-ui__recipient-summary">
                {{ rowViewModels[rowIndex].recipient.propertySummary }}
              </span>
            </div>
          </template>

          <template #col-2="{ rowIndex }">
            <div v-if="rowViewModels[rowIndex]" class="automated-email-outbox-ui__reason-cell">
              <span
                :class="[
                  'automated-email-outbox-ui__trigger-badge',
                  `automated-email-outbox-ui__trigger-badge--${triggerVariant(
                    rowViewModels[rowIndex].reason.triggerLabel
                  )}`,
                ]"
              >
                {{ rowViewModels[rowIndex].reason.triggerLabel }}
              </span>
              <span class="automated-email-outbox-ui__reason-address">
                {{ rowViewModels[rowIndex].reason.address }}
              </span>
              <span class="automated-email-outbox-ui__reason-summary">
                {{ rowViewModels[rowIndex].reason.listingSummary }}
              </span>
            </div>
          </template>

          <template #col-3="{ rowIndex }">
            <div v-if="rowViewModels[rowIndex]" class="automated-email-outbox-ui__subject-cell">
              <span class="automated-email-outbox-ui__subject">
                {{ rowViewModels[rowIndex].subject.subject || '-' }}
              </span>
              <span class="automated-email-outbox-ui__subject-preview">
                {{ rowViewModels[rowIndex].subject.preview }}
              </span>
            </div>
          </template>

          <template #col-4="{ colData }">
            <span class="automated-email-outbox-ui__countdown">{{ getSendingCountdownLabel(colData) }}</span>
          </template>

          <template #col-5="{ colData }">
            <div class="automated-email-outbox-ui__row-actions" @click.stop>
              <VBtn flat class="auto-email-table-action" @click="sendEmails([{ id: colData }])">
                {{ isEmailFailed(colData) ? 'Retry' : 'Send' }}
              </VBtn>
              <VBtn flat class="auto-email-table-action" @click="dismissEmails([colData])">
                Delete
              </VBtn>
            </div>
          </template>

          <template #no-data-message>
            <VFallbackText class="auto-email-outbox">
              Triggers run once daily. Come back after the next scheduled run to review and send
              emails to your contacts.
            </VFallbackText>
          </template>
        </VTable>
      </div>

      <div v-if="pagination" class="automated-email-outbox-ui__pagination">
        <VPagination :pagination="pagination" @on-go-to-page="fetchOutbox" />
      </div>
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { ComputedRef, Ref } from 'vue';
  import type { ITable, TTableData } from '~/misc/v-table/types';
  import type { TOutboxEmail } from '~/modules/opportunities/automated-email/types';
  import type { TAutomatedEmailOutboxFilters } from '~/modules/opportunities/automated-email/outbox/composables/use-outbox-filters/types';
  import type { TOutboxRowViewModel } from '~/modules/opportunities/automated-email/outbox/types';

  // Config
  import { TABLE_HEADERS, TRIGGER_FILTERS } from '~/modules/opportunities/automated-email/outbox/config';

  // Services
  import { AutomatedEmailTriggerVariantService } from '~/modules/opportunities/automated-email/services/trigger-variant';

  // Utils
  import {
    getFormattedSendingCountdown,
    getOutboxSendAtIsoForCountdown,
  } from '~/modules/opportunities/automated-email/outbox/utils/format-sending-countdown';
  import { buildOutboxRowViewModel } from '~/modules/opportunities/automated-email/outbox/utils/format-outbox-cells';

  // Composables
  import { useAutomatedEmailOutboxFilters } from '~/modules/opportunities/automated-email/outbox/composables/use-outbox-filters';
  import { useOutbox } from '~/modules/opportunities/automated-email/outbox/composables/use-outbox';
  import useModal from '~/composables/use-modal';

  // Components
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import VBtn from '~/misc/v-btn';
  import VCheckbox from '~/misc/v-checkbox';
  import VTable from '~/misc/v-table';
  import VTableHeader from '~/misc/v-table-header/VTableHeader.vue';
  import VPagination from '~/misc/v-pagination/VPagination.vue';

  const OutboxEmailModal = defineAsyncComponent(
    () => import('~/modules/opportunities/automated-email/outbox/email-modal/OutboxEmailModal.vue')
  );

  const filters: TAutomatedEmailOutboxFilters = useAutomatedEmailOutboxFilters();

  const { patchId, saleType, filterTriggerName, setFilterTriggerName } = filters;

  const {
    emails,
    pagination,
    isLoading,
    isSending,
    selectedCount,
    hasSelection,
    allSelected,
    isSelected,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    fetchOutbox,
    sendEmails,
    dismissEmails,
    sendSelected,
  } = useOutbox(filters);

  const { show: showModal } = useModal();

  const hasDisplayEmails: ComputedRef<boolean> = computed<boolean>(() => emails.value.length > 0);

  const shouldShowEmptyListHint: ComputedRef<boolean> = computed<boolean>(
    () => filterTriggerName.value !== null
  );

  const emailStatusById: ComputedRef<Map<number, string | undefined>> = computed<
    Map<number, string | undefined>
  >(
    () =>
      new Map<number, string | undefined>(
        emails.value.map((email: TOutboxEmail) => [email.id, email.status])
      )
  );

  const isEmailFailed = (id: number): boolean => emailStatusById.value.get(id) === 'failed';

  const rowViewModels: ComputedRef<TOutboxRowViewModel[]> = computed<TOutboxRowViewModel[]>(() =>
    emails.value.map(buildOutboxRowViewModel)
  );

  const countdownNowMs: Ref<number> = ref<number>(Date.now());

  const setCountdownNowMs = (ms: number): void => {
    countdownNowMs.value = ms;
  };

  const countdownIntervalId: Ref<ReturnType<typeof setInterval> | undefined> = ref<
    ReturnType<typeof setInterval> | undefined
  >(undefined);

  const setCountdownIntervalId = (
    id: ReturnType<typeof setInterval> | undefined
  ): void => {
    countdownIntervalId.value = id;
  };

  const tableData: ComputedRef<TTableData> = computed<TTableData>(() =>
    emails.value.map((email: TOutboxEmail) => [
      email.id,
      email.id,
      email.id,
      email.id,
      getOutboxSendAtIsoForCountdown(email),
      email.id,
    ])
  );

  const table: ComputedRef<ITable> = computed<ITable>(() => ({
    headers: [],
    data: tableData.value,
  }));

  const triggerVariant = (trigger: string) =>
    AutomatedEmailTriggerVariantService.resolve(trigger);

  const handleTableRowClick = (event: MouseEvent): void => {
    const target: HTMLElement = event.target as HTMLElement;
    const row: Element | null = target.closest('.v-table-ui__row');

    if (!row) {
      return;
    }

    const rows: Element[] = Array.from(
      row.parentElement?.querySelectorAll('.v-table-ui__row') ?? []
    );
    const rowIndex: number = rows.indexOf(row) - 1;

    if (rowIndex < 0 || rowIndex >= emails.value.length) {
      return;
    }

    const email: TOutboxEmail = emails.value[rowIndex];

    openEmailModal(email);
  };

  const getSendingCountdownLabel = (sendAtIso: string): string =>
    getFormattedSendingCountdown(sendAtIso || null, countdownNowMs.value);

  const openEmailModal = (email: TOutboxEmail): void => {
    showModal(OutboxEmailModal, {
      email,
      sendCallback: sendEmails,
      dismissCallback: dismissEmails,
      isSending: isSending.value,
    });
  };

  const refetchFirstPage = (): void => {
    fetchOutbox(1).catch(() => undefined);
  };

  watch(filterTriggerName, (): void => {
    clearSelection();
    refetchFirstPage();
  });

  watch(
    [patchId, saleType],
    ([nextPatchId, nextSaleType], [previousPatchId, previousSaleType]) => {
      if (
        nextPatchId === undefined ||
        (nextPatchId === previousPatchId && nextSaleType === previousSaleType)
      ) {
        return;
      }

      clearSelection();
      refetchFirstPage();
    }
  );

  onMounted(() => {
    if (patchId.value !== undefined) {
      fetchOutbox();
    }
    setCountdownIntervalId(
      setInterval((): void => {
        setCountdownNowMs(Date.now());
      }, 1000)
    );
  });

  onUnmounted(() => {
    if (countdownIntervalId.value !== undefined) {
      clearInterval(countdownIntervalId.value);
      setCountdownIntervalId(undefined);
    }
  });
</script>
